import ApplicationController from "./application_controller";

/**
 * Overlay modal that is present on page load (e.g. the booking confirmation).
 * Locks body scroll, moves focus into the dialog so it and its label are
 * announced, traps Tab while open, and closes on Escape / overlay click / the
 * close button (WCAG 2.4.3 Focus Order, 2.1.2 No Keyboard Trap).
 *
 * Mark the dialog card with data-modal-overlay-target="dialog" (and role="dialog"
 * + aria-modal + a label in the template); the controller element is the overlay.
 */
export default class extends ApplicationController {
    static targets = ["dialog"];

    connect() {
        document.body.classList.add("overflow-hidden");

        // Remember what had focus so we can restore it on close (WCAG 2.4.3) —
        // e.g. the "Cancel booking" trigger that HTMX-injected this modal.
        this.previouslyFocused = document.activeElement;

        const dialog = this.dialogElement();
        if (!dialog.hasAttribute("tabindex")) {
            dialog.setAttribute("tabindex", "-1");
        }
        // Focus the dialog itself so screen readers announce its role + label
        // (the confirmation heading) rather than landing on the close button.
        dialog.focus();

        this.boundKeydown = this.handleKeydown.bind(this);
        document.addEventListener("keydown", this.boundKeydown);
    }

    disconnect() {
        document.body.classList.remove("overflow-hidden");
        if (this.boundKeydown) {
            document.removeEventListener("keydown", this.boundKeydown);
            this.boundKeydown = null;
        }
    }

    close() {
        const toRestore = this.previouslyFocused;
        this.element.remove();
        // Return focus to the trigger if it's still in the document (a successful
        // cancel removes it via an out-of-band swap — then we leave focus be and
        // the swapped-in status message announces itself).
        if (toRestore && toRestore.isConnected && typeof toRestore.focus === "function") {
            toRestore.focus();
        }
    }

    closeOnOverlay(event) {
        if (event.target === this.element) {
            this.close();
        }
    }

    handleKeydown(event) {
        if (event.key === "Escape") {
            this.close();
            return;
        }
        if (event.key !== "Tab") return;

        const focusable = this.getFocusable();
        if (focusable.length === 0) {
            event.preventDefault();
            return;
        }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = document.activeElement;

        if (event.shiftKey && (active === first || active === this.dialogElement())) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && active === last) {
            event.preventDefault();
            first.focus();
        }
    }

    dialogElement() {
        return this.hasDialogTarget ? this.dialogTarget : this.element;
    }

    getFocusable() {
        const selector =
            'a[href], button:not([disabled]), input:not([disabled]), ' +
            'select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
        return Array.from(this.dialogElement().querySelectorAll(selector)).filter(
            (el) => el.getClientRects().length > 0
        );
    }
}
